import { Card, Heading, Text } from '@ui/themed';
import { t } from 'i18next';
import { getCountryName } from '../../../hooks/use-countries';
import type { CustomerAddress } from '../types/customer';
import { TwoColumnLayout } from './two-column-layout';

function AddressBlock({ address }: { address: CustomerAddress }) {
  const addressLines = [
    { id: 'line1', value: address.address_line1 },
    { id: 'line2', value: address.address_line2 },
    { id: 'city', value: address.city },
    { id: 'postal', value: address.postal_code },
    { id: 'country', value: getCountryName(address.country_code) },
    { id: 'phone', value: address.phone },
  ];
  return (
    <div className="flex flex-col gap-0.5">
      <Text>
        {address.first_name} {address.last_name}
      </Text>
      {addressLines
        .filter((line) => Boolean(line.value))
        .map((line) => (
          <Text key={line.id}>{line.value}</Text>
        ))}
    </div>
  );
}

function DefaultAddresses({
  shipping,
  billing,
}: {
  shipping?: CustomerAddress;
  billing?: CustomerAddress;
}) {
  if (shipping && billing) {
    return (
      <TwoColumnLayout>
        <TwoColumnLayout.Column>
          <Heading level="h3" style="sm">
            {t('features:customers.components.customer_addresses_card.default_shipping')}
          </Heading>
          <AddressBlock address={shipping} />
        </TwoColumnLayout.Column>
        <TwoColumnLayout.Column>
          <Heading level="h3" style="sm">
            {t('features:customers.components.customer_addresses_card.default_billing')}
          </Heading>
          <AddressBlock address={billing} />
        </TwoColumnLayout.Column>
      </TwoColumnLayout>
    );
  }
  return [
    {
      name: t('features:customers.components.customer_addresses_card.default_shipping'),
      value: shipping,
    },
    {
      name: t('features:customers.components.customer_addresses_card.default_billing'),
      value: billing,
    },
  ]
    .sort((a, b) => {
      if (a.value === undefined && b.value !== undefined) return -1;
      if (a.value !== undefined && b.value === undefined) return 1;
      return 0;
    })
    .map((address) => {
      return (
        <TwoColumnLayout key={address.name}>
          <TwoColumnLayout.Column>
            <Text>{address.name}</Text>
          </TwoColumnLayout.Column>
          <TwoColumnLayout.Column>
            {address.value ? (
              <AddressBlock address={address.value} />
            ) : (
              <Text variant="secondary">{t('labels:none_selected')}</Text>
            )}
          </TwoColumnLayout.Column>
        </TwoColumnLayout>
      );
    });
}

export function CustomerAddressesCard({ addresses }: { addresses: CustomerAddress[] }) {
  const defaultShippingAddress = addresses.find((address) => address.is_default_shipping);
  const defaultBillingAddress = addresses.find((address) => address.is_default_billing);
  const otherAddresses = addresses.filter(
    (address) =>
      address.id !== defaultShippingAddress?.id && address.id !== defaultBillingAddress?.id,
  );

  return (
    <Card>
      <Card.Header headingLevel="h2">
        {t('features:customers.components.customer_addresses_card.header')}
      </Card.Header>
      <Card.Body>
        <DefaultAddresses shipping={defaultShippingAddress} billing={defaultBillingAddress} />
        {otherAddresses.length > 0 && (
          <div className="flex flex-col gap-2">
            <Heading level="h3" style="sm">
              {t('features:customers.components.customer_addresses_card.other_addresses')}
            </Heading>
            <TwoColumnLayout>
              {otherAddresses.map((address) => (
                <AddressBlock key={address.id} address={address} />
              ))}
            </TwoColumnLayout>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
